import type { SidebarSectionGroup } from '@/elements/research/components/sidebarSections';
import {
  type CustomGroupDef,
  type CustomGroupId,
  DEFAULT_SIDEBAR_LAYOUT_STATE,
  type LayoutGroupId,
  SIDEBAR_LAYOUT_SCHEMA_VERSION,
  type SidebarLayoutState,
} from '@/types/sidebarLayout';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Sidebar Layout Redux Slice with localStorage Persistence
 *
 * Persists the user's custom sidebar structure (item order, group
 * assignment, group renames, custom groups, hidden items) — localStorage
 * only, no backend sync, following the exact getStored/save pattern already
 * used by uiSettingsSlice.ts.
 */

// Exported (not just for the reducers below) so the corrupt/version-mismatch
// fallback path can be exercised directly in tests — the module-level
// `initialState` below is computed once at import time, so a test can't
// re-trigger it by seeding localStorage after the fact.
export const SIDEBAR_LAYOUT_STORAGE_KEY = 'lexomat_sidebar_layout';

// Minimal runtime shape check — enough to catch corrupted/foreign JSON
// without hand-rolling a full schema validator for a low-stakes cosmetic
// preference. Any failure here wipes the key and falls back to defaults,
// mirroring the "corrupt -> reset" strategy uiSettingsSlice.ts already uses
// for corrupted perspective/gear data.
export const isValidSidebarLayoutState = (
  value: unknown
): value is SidebarLayoutState => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['version'] === 'number' &&
    candidate['version'] === SIDEBAR_LAYOUT_SCHEMA_VERSION &&
    typeof candidate['groupRenames'] === 'object' &&
    candidate['groupRenames'] !== null &&
    Array.isArray(candidate['customGroups']) &&
    Array.isArray(candidate['groupOrder']) &&
    typeof candidate['items'] === 'object' &&
    candidate['items'] !== null
  );
};

export const getStoredSidebarLayout = (): SidebarLayoutState => {
  if (typeof window === 'undefined') return DEFAULT_SIDEBAR_LAYOUT_STATE;
  try {
    const stored = localStorage.getItem(SIDEBAR_LAYOUT_STORAGE_KEY);
    if (!stored) return DEFAULT_SIDEBAR_LAYOUT_STATE;
    const parsed: unknown = JSON.parse(stored);
    if (!isValidSidebarLayoutState(parsed)) {
      localStorage.removeItem(SIDEBAR_LAYOUT_STORAGE_KEY);
      return DEFAULT_SIDEBAR_LAYOUT_STATE;
    }
    return parsed;
  } catch (error) {
    console.error('Failed to parse stored sidebar layout:', error);
    localStorage.removeItem(SIDEBAR_LAYOUT_STORAGE_KEY);
    return DEFAULT_SIDEBAR_LAYOUT_STATE;
  }
};

const saveSidebarLayout = (state: SidebarLayoutState): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SIDEBAR_LAYOUT_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save sidebar layout to localStorage:', error);
  }
};

const initialState: SidebarLayoutState = getStoredSidebarLayout();

const sidebarLayoutSlice = createSlice({
  name: 'sidebarLayout',
  initialState,
  reducers: {
    /**
     * Replaces the full ordered key list for one group after a drag-and-drop
     * settles — used for BOTH a same-group reorder and a cross-group move
     * (the destination group's post-drop order already reflects the moved
     * item's new position, so a single reducer covers both cases). Order
     * values only need to be relatively correct within a group (the resolver
     * sorts by `order`), so the SOURCE group's leftover items never need
     * reindexing when an item leaves it.
     */
    setGroupItemOrder(
      state,
      action: PayloadAction<{ groupId: LayoutGroupId; orderedKeys: string[] }>
    ) {
      const { groupId, orderedKeys } = action.payload;
      orderedKeys.forEach((key, index) => {
        const existing = state.items[key];
        state.items[key] = { ...existing, group: groupId, order: index };
      });
      saveSidebarLayout(state);
    },
    /** Renames a built-in group (including 'top'). Empty string clears the override, falling back to the default i18n label. */
    renameGroup(
      state,
      action: PayloadAction<{ group: SidebarSectionGroup; label: string }>
    ) {
      const { group, label } = action.payload;
      const trimmed = label.trim();
      if (trimmed) {
        state.groupRenames[group] = trimmed;
      } else {
        delete state.groupRenames[group];
      }
      saveSidebarLayout(state);
    },
    /** Renames a custom group. No-op if the group doesn't exist or the label is blank (mirrors renameGroup's empty-string handling, but a custom group always needs a non-empty label so blank is simply rejected rather than clearing to a default). */
    renameCustomGroup(
      state,
      action: PayloadAction<{ id: CustomGroupId; label: string }>
    ) {
      const trimmed = action.payload.label.trim();
      if (!trimmed) return;
      const def = state.customGroups.find((g) => g.id === action.payload.id);
      if (!def) return;
      def.label = trimmed;
      saveSidebarLayout(state);
    },
    /** Creates a new custom group. The id is generated by the caller (crypto.randomUUID()-based) so it can immediately move an item into it. */
    createGroup(
      state,
      action: PayloadAction<{ id: CustomGroupId; label: string }>
    ) {
      const label = action.payload.label.trim();
      if (!label) return;
      const def: CustomGroupDef = { id: action.payload.id, label };
      state.customGroups.push(def);
      state.groupOrder.push(def.id);
      saveSidebarLayout(state);
    },
    /** Deletes a custom group. Every item assigned to it has its group override cleared, so it falls back to its section's default built-in group. Built-in groups are not deletable. */
    deleteGroup(state, action: PayloadAction<{ id: CustomGroupId }>) {
      const { id } = action.payload;
      state.customGroups = state.customGroups.filter((g) => g.id !== id);
      state.groupOrder = state.groupOrder.filter((g) => g !== id);
      for (const [key, override] of Object.entries(state.items)) {
        if (override.group === id) {
          delete override.group;
          delete override.order;
          if (!override.hidden) delete state.items[key];
        }
      }
      saveSidebarLayout(state);
    },
    /** Upserts an item's hidden flag without touching any existing group/order override. */
    setItemHidden(
      state,
      action: PayloadAction<{ key: string; hidden: boolean }>
    ) {
      const { key, hidden } = action.payload;
      const existing = state.items[key];
      state.items[key] = { ...existing, hidden };
      saveSidebarLayout(state);
    },
    /** Reorders the groups themselves (drag on group headers in the editor). */
    setGroupOrder(state, action: PayloadAction<LayoutGroupId[]>) {
      state.groupOrder = action.payload;
      saveSidebarLayout(state);
    },
    /** Restores the shipped default layout — clears every customization. */
    resetToDefault(state) {
      state.groupRenames = {};
      state.customGroups = [];
      state.groupOrder = [];
      state.items = {};
      saveSidebarLayout(state);
    },
  },
});

export const {
  setGroupItemOrder,
  renameGroup,
  renameCustomGroup,
  createGroup,
  deleteGroup,
  setItemHidden,
  setGroupOrder,
  resetToDefault,
} = sidebarLayoutSlice.actions;

export default sidebarLayoutSlice.reducer;
